/**
 * ProfilePage Component - Own Profile View
 * Similar to PublicProfilePage but with additional features:
 * - Email always visible with visibility toggle
 * - Edit Profile button
 * - Remove buttons on collaborated posts
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import apiClient from '../api/client';
import Navbar from '../components/Navbar';
import Gallery from '../components/Gallery';
import ProfilePostCard from '../components/ProfilePostCard';
import '../styles/colors.css';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [fullUser, setFullUser] = useState(null); // Full user data with linked accounts
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchFullUserData();
      fetchUserPosts();
      setLoading(false);
    }
  }, [user]);

  // Fetch complete user data including linked accounts
  const fetchFullUserData = async () => {
    if (!user) return;
    
    try {
      const response = await apiClient.get(`/users/${user.id}`);
      const userData = response.data.user || response.data;
      setFullUser(userData);
    } catch (err) {
      console.error('Failed to fetch full user data:', err);
    }
  };

  const fetchUserPosts = async () => {
    if (!user) return;
    
    try {
      const userId = user.id;
      console.log('ProfilePage: Fetching posts for user ID:', userId);
      
      // Fetch all posts and filter for this user
      const [eventsRes, articlesRes, oppsRes] = await Promise.all([
        apiClient.get('/events'),
        apiClient.get('/articles'),
        apiClient.get('/opportunities')
      ]);
      
      console.log('ProfilePage: Raw events:', eventsRes.data.events || eventsRes.data);
      
      // Events: created by user OR user is accepted collaborator (and not removed)
      const events = (eventsRes.data.events || eventsRes.data || [])
        .filter(e => {
          const isCreator = e.created_by === userId;
          const isCollaborator = e.collaborators && e.collaborators.some(
            c => {
              console.log('Event', e.id, 'collab check:', c.user_id, '===', userId, '&& status:', c.status);
              return c.user_id === userId && c.status === 'accepted';
            }
          );
          const isRemoved = e.collaborators && e.collaborators.some(
            c => c.user_id === userId && c.status === 'removed'
          );
          const include = (isCreator || isCollaborator) && !isRemoved;
          console.log('Event', e.id, '- isCreator:', isCreator, ', isCollaborator:', isCollaborator, ', isRemoved:', isRemoved, ', include:', include);
          return include;
        })
        .map(e => ({ ...e, post_type: 'event', isCreator: e.created_by === userId }));
      
      // Articles: created by user OR user is accepted collaborator (and not removed)
      const articles = (articlesRes.data.articles || articlesRes.data || [])
        .filter(a => {
          const isCreator = a.created_by === userId;
          const isCollaborator = a.collaborators && a.collaborators.some(
            c => c.user_id === userId && c.status === 'accepted'
          );
          const isRemoved = a.collaborators && a.collaborators.some(
            c => c.user_id === userId && c.status === 'removed'
          );
          return (isCreator || isCollaborator) && !isRemoved;
        })
        .map(a => ({ ...a, post_type: 'article', isCreator: a.created_by === userId }));
      
      // Opportunities: created by user OR user is accepted collaborator (and not removed)
      const opportunities = (oppsRes.data.opportunities || oppsRes.data || [])
        .filter(o => {
          const isCreator = o.created_by === userId;
          const isCollaborator = o.collaborators && o.collaborators.some(
            c => c.user_id === userId && c.status === 'accepted'
          );
          const isRemoved = o.collaborators && o.collaborators.some(
            c => c.user_id === userId && c.status === 'removed'
          );
          return (isCreator || isCollaborator) && !isRemoved;
        })
        .map(o => ({ ...o, post_type: 'opportunity', isCreator: o.created_by === userId }));
      
      console.log('ProfilePage: Filtered events:', events);
      console.log('ProfilePage: Filtered articles:', articles);
      console.log('ProfilePage: Filtered opportunities:', opportunities);
      
      const allPosts = [...events, ...articles, ...opportunities];
      allPosts.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      
      console.log('ProfilePage: All posts combined:', allPosts);
      
      setPosts(allPosts);
    } catch (err) {
      console.error('Failed to load posts:', err);
    }
  };

  const handleRemoveCollaboration = async (post) => {
    if (!window.confirm('Remove yourself from this collaboration?')) {
      return;
    }

    try {
      // Fetch collaborators to find current user's collaboration record ID
      let collabEndpoint = '';
      
      if (post.post_type === 'event') {
        collabEndpoint = `/events/${post.id}/collaborators`;
      } else if (post.post_type === 'article') {
        collabEndpoint = `/articles/${post.id}/collaborators`;
      } else if (post.post_type === 'opportunity') {
        collabEndpoint = `/opportunities/${post.id}/collaborators`;
      }

      const collabResponse = await apiClient.get(collabEndpoint);
      if (!collabResponse.data.success) {
        throw new Error('Failed to fetch collaborators');
      }

      // Find the collaboration record for current user
      const userCollaboration = collabResponse.data.collaborators.find(
        c => c.collaborator_id === user.id
      );

      if (!userCollaboration) {
        throw new Error('Collaboration not found');
      }

      // Update status to 'removed' using PUT endpoint
      let statusEndpoint = '';
      if (post.post_type === 'event') {
        statusEndpoint = `/events/collaborators/${userCollaboration.id}`;
      } else if (post.post_type === 'article') {
        statusEndpoint = `/articles/collaborators/${userCollaboration.id}`;
      } else if (post.post_type === 'opportunity') {
        statusEndpoint = `/opportunities/collaborators/${userCollaboration.id}`;
      }

      await apiClient.put(statusEndpoint, { status: 'removed' });
      
      alert('Successfully removed from collaboration');
      // Refresh posts to hide the removed collaboration
      fetchUserPosts();
    } catch (err) {
      console.error('Failed to remove collaboration:', err);
      alert('Failed to remove collaboration');
    }
  };

  const getRoleBadgeText = (role) => {
    const roleMap = {
      'band': 'Band/Artist',
      'venue': 'Venue',
      'promoter': 'Promoter',
      'photographer': 'Photographer',
      'vendor': 'Vendor',
      'studio': 'Recording Studio',
      'fan': 'Fan'
    };
    return roleMap[role] || role;
  };

  const formatVenueTypes = (types) => {
    if (!types || types.length === 0) return '';
    return types.map(t => t.charAt(0).toUpperCase() + t.slice(1)).join(', ');
  };

  const formatAgeRestriction = (age) => {
    if (age === 'all_ages') return 'All Ages';
    if (age === '18_plus') return '18+';
    if (age === '21_plus') return '21+';
    return age;
  };

  if (loading || !user) {
    return (
      <>
        <Navbar />
        <div style={{ minHeight: '100vh', background: 'var(--bg-dark)', color: 'var(--text-light)', padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <div style={{ fontSize: '18px' }}>Loading profile...</div>
        </div>
      </>
    );
  }

  // Use fullUser for display (includes linked accounts), fallback to user from auth
  const displayUser = fullUser || user;

  console.log('ProfilePage user data:', displayUser);
  console.log('Profile image URL:', displayUser.profile_image_url);

  const hasGalleryImages = displayUser.image_gallery_urls && displayUser.image_gallery_urls.length > 0;
  const hasPosts = posts && posts.length > 0;

  return (
    <>
      <style>{`
        @media (max-width: 768px) {
          .profile-page-container {
            padding-top: 125px !important;
          }

          .profile-edit-button-row {
            position: absolute !important;
            top: 60px !important;
            right: 20px !important;
            margin-bottom: 0 !important;
          }

          .profile-email-section {
            position: absolute !important;
            top: 125px !important;
            right: 20px !important;
            text-align: right !important;
          }

          .profile-header-main {
            margin-top: 60px !important;
          }

          .profile-two-column {
            display: flex !important;
            flex-direction: column !important;
          }

          .profile-info-text {
            font-size: 16px !important;
          }

          .profile-genre-tags {
            margin-top: 32px !important;
            padding-top: 24px !important;
            border-top: 1px solid var(--border-light) !important;
          }

          .profile-posts-grid {
            grid-template-columns: 1fr !important;
          }
        }

        .profile-gallery-hide-title {
          display: none !important;
        }

        .profile-gallery-section {
          align-self: flex-start !important;
        }
      `}</style>
      <Navbar />
      <div className="profile-page-container" style={{ minHeight: '100vh', background: 'var(--bg-dark)', paddingTop: '140px', paddingBottom: '40px', paddingLeft: '20px', paddingRight: '20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
          
          {/* Edit Profile Button Row */}
          <div className="profile-edit-button-row" style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0' }}>
            <button
              onClick={() => navigate('/profile/edit')}
              className="btn btn-primary"
            >
              Edit Profile
            </button>
          </div>

          {/* Header Section - NO BOX */}
          <div className="profile-header-main" style={{ marginBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px', flexWrap: 'wrap' }}>

              {/* Profile Image */}
              <div style={{ flexShrink: 0 }}>
                {displayUser.profile_image_url ? (
                  <img 
                    src={displayUser.profile_image_url} 
                    alt={displayUser.display_name}
                    style={{ width: '120px', height: '120px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--accent-primary)' }}
                  />
                ) : (
                  <div style={{ width: '120px', height: '120px', borderRadius: '50%', background: 'var(--smoke-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '48px', fontWeight: '700', color: 'var(--text-light)' }}>
                    {displayUser.display_name.charAt(0).toUpperCase()}
                  </div>
                )}
              </div>

              {/* Name, Role, Location */}
              <div style={{ flex: 1, minWidth: '250px' }}>
                <h1 style={{ margin: '0 0 12px 0', fontSize: '32px', fontWeight: '700', color: 'var(--text-light)' }}>
                  {displayUser.display_name}
                </h1>
                
                <div style={{ marginBottom: '12px' }}>
                  <span className="badge badge-primary" style={{ fontSize: '13px' }}>
                    {getRoleBadgeText(displayUser.account_role)}
                  </span>
                </div>

                {displayUser.city && displayUser.state && (
                  <div style={{ fontSize: '16px', color: 'var(--text-light)', marginBottom: '4px', opacity: 0.8 }}>
                    📍 {displayUser.city}, {displayUser.state}
                  </div>
                )}
              </div>

              {/* Email (Always Visible) + Visibility Toggle */}
              <div className="profile-email-section" style={{ textAlign: 'right', minWidth: '200px' }}>
                <div style={{ fontSize: '13px', color: 'var(--text-light)', marginBottom: '4px', opacity: 0.6 }}>Email</div>
                <a href={`mailto:${displayUser.email}`} style={{ fontSize: '15px', color: 'var(--accent-primary)', wordBreak: 'break-all', display: 'block', marginBottom: '8px' }}>
                  {displayUser.email}
                </a>
              </div>
            </div>
          </div>

          {/* Two-Column Layout: Bio/Info (Left) | Gallery (Right) - ONLY BIO/INFO IS BOXED */}
          <div className="profile-two-column" style={{ display: 'grid', gridTemplateColumns: hasGalleryImages ? '1fr 1fr' : '1fr', gap: '24px', marginBottom: '40px' }}>
            
            {/* LEFT COLUMN: Bio and Role-Specific Info - WHITE BOX */}
            <div style={{ background: 'var(--bg-card)', borderRadius: '8px', padding: '32px', boxShadow: 'var(--shadow-card)' }}>
              
              {/* Bio */}
              {displayUser.bio && (
                <div style={{ marginBottom: '32px' }}>
                  <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-dark)' }}>About</h3>
                  <p className="profile-info-text" style={{ margin: 0, fontSize: '15px', lineHeight: '1.6', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                    {displayUser.bio}
                  </p>
                </div>
              )}

              {/* Role-Specific Information */}
              <div>
                
                {/* BAND/ARTIST */}
                {displayUser.account_role === 'band' && (
                  <>
                    {displayUser.travel_distance && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {displayUser.travel_distance} miles
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* VENUE */}
                {displayUser.account_role === 'venue' && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      {displayUser.venue_type && displayUser.venue_type.length > 0 && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Venue Type</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {formatVenueTypes(displayUser.venue_type)}
                          </div>
                        </div>
                      )}

                      {displayUser.age_restriction && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Age Restriction</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {formatAgeRestriction(displayUser.age_restriction)}
                          </div>
                        </div>
                      )}
                    </div>

                    {displayUser.address && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {displayUser.address}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                      {displayUser.capacity && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Capacity</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {displayUser.capacity} people
                          </div>
                        </div>
                      )}

                      {displayUser.gear_available && (
                        <div>
                          <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gear Available</h4>
                          <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                            {displayUser.gear_available}
                          </div>
                        </div>
                      )}
                    </div>

                    {displayUser.address && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Address</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {displayUser.address}
                        </div>
                      </div>
                    )}

                    {displayUser.bookers_associated && displayUser.bookers_associated.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Associated Promoters</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {displayUser.bookers_associated.map((booker, idx) => (
                            <a 
                              key={idx} 
                              href={`/users/${booker.id}`}
                              style={{ fontSize: '15px', color: 'var(--accent-primary)' }}
                            >
                              {booker.name || booker.username}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* PHOTOGRAPHER, VENDOR, STUDIO */}
                {['photographer', 'vendor', 'studio'].includes(displayUser.account_role) && (
                  <>
                    {displayUser.travel_distance && displayUser.account_role !== 'studio' && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {displayUser.travel_distance} miles
                        </div>
                      </div>
                    )}

                    {displayUser.services && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Services</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                          {displayUser.services}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* PROMOTER */}
                {displayUser.account_role === 'promoter' && (
                  <>
                    {displayUser.travel_distance && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Travel Radius</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)' }}>
                          {displayUser.travel_distance} miles
                        </div>
                      </div>
                    )}

                    {displayUser.regions_covered && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Regions Covered</h4>
                        <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                          {displayUser.regions_covered}
                        </div>
                      </div>
                    )}

                    {displayUser.venues_associated && displayUser.venues_associated.length > 0 && (
                      <div style={{ marginBottom: '20px' }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Associated Venues</h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {displayUser.venues_associated.map((venue, idx) => (
                            <a 
                              key={idx} 
                              href={`/users/${venue.id}`}
                              style={{ fontSize: '15px', color: 'var(--accent-primary)' }}
                            >
                              {venue.name || venue.username}
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Links (all roles) */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {displayUser.website_url && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Website</h4>
                      <a href={displayUser.website_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)' }}>
                        {displayUser.website_url}
                      </a>
                    </div>
                  )}

                  {displayUser.social_links && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Links</h4>
                      <div className="profile-info-text" style={{ fontSize: '15px', color: 'var(--text-dark)', whiteSpace: 'pre-wrap' }}>
                        {displayUser.social_links}
                      </div>
                    </div>
                  )}

                  {(displayUser.instagram_url || displayUser.facebook_url || displayUser.tiktok_url) && (
                    <div>
                      <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Social Media</h4>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        {displayUser.instagram_url && (
                          <a href={displayUser.instagram_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>📷</span> Instagram
                          </a>
                        )}
                        {displayUser.facebook_url && (
                          <a href={displayUser.facebook_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>👥</span> Facebook
                          </a>
                        )}
                        {displayUser.tiktok_url && (
                          <a href={displayUser.tiktok_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '15px', color: 'var(--accent-primary)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span>🎵</span> TikTok
                          </a>
                        )}
                      </div>
                    </div>
                  )}

                  {displayUser.preferred_contact_method && (
                    <div>
                      <h4 style={{ margin: '0 0 4px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Preferred Contact</h4>
                      <div style={{ fontSize: '15px', color: 'var(--text-dark)', textTransform: 'capitalize' }}>
                        {displayUser.preferred_contact_method}
                      </div>
                    </div>
                  )}
                </div>

                {/* Genre Tags at Bottom (Bands only) */}
                {displayUser.account_role === 'band' && displayUser.genre_tags && displayUser.genre_tags.length > 0 && (
                  <div className="profile-genre-tags" style={{ marginTop: 0 }}>
                    <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Genres</h4>
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      {displayUser.genre_tags.map((genre, idx) => (
                        <span key={idx} className="badge badge-outline" style={{ fontSize: '12px' }}>
                          {genre}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* RIGHT COLUMN: Gallery (if exists) - NO BOX */}
            {hasGalleryImages && (
              <div className="profile-gallery-section">
                <h3 className="profile-gallery-hide-title" style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: 'var(--text-light)' }}>Gallery</h3>
                <Gallery images={displayUser.image_gallery_urls} />
              </div>
            )}
          </div>

          {/* Posts Section (Full Width Below) - NO BOX */}
          {hasPosts && (
            <div>
              <h2 style={{ margin: '0 0 20px 0', fontSize: '24px', fontWeight: '700', color: 'var(--text-light)' }}>
                Posts
              </h2>
              <div className="profile-posts-grid" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
                gap: '20px' 
              }}>
                {posts.map(post => (
                  <ProfilePostCard 
                    key={post.id} 
                    post={post}
                    showRemoveButton={!post.isCreator}
                    onRemove={handleRemoveCollaboration}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No Posts Message */}
          {!hasPosts && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '16px', color: 'var(--text-light)', opacity: 0.6 }}>
                No posts yet
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfilePage;
